/**
 * ModuleSearch (#007)
 *
 * A busca da appbar mobile deve abrir a busca DO MÓDULO atual (ex: "Buscar
 * leads…"), não o CommandPalette de navegação. Cada página de listagem
 * registra sua busca via `useModuleSearch({ placeholder, value, onChange })`;
 * o botão de busca da appbar abre o `ModuleSearchOverlay` (barra de busca no
 * topo, live-filter na lista abaixo). Quando a rota atual NÃO registra busca,
 * o botão cai no CommandPalette (fallback — busca nunca fica morta).
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Icon } from '@/shared/icons';

export interface ModuleSearchConfig {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}

interface ModuleSearchContextValue {
  config: ModuleSearchConfig | null;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  register: (config: ModuleSearchConfig) => void;
  unregister: () => void;
}

const ModuleSearchContext = createContext<ModuleSearchContextValue | null>(null);

export const ModuleSearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<ModuleSearchConfig | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const register = useCallback((next: ModuleSearchConfig) => setConfig(next), []);
  const unregister = useCallback(() => {
    setConfig(null);
    setIsOpen(false);
  }, []);

  // Value memoizado: identidade só muda quando config/isOpen mudam — evita que
  // consumidores que dependem das funções estáveis re-executem à toa.
  const value = useMemo(
    () => ({ config, isOpen, open, close, register, unregister }),
    [config, isOpen, open, close, register, unregister],
  );

  return <ModuleSearchContext.Provider value={value}>{children}</ModuleSearchContext.Provider>;
};

/** Controle consumido pela appbar/overlay. Pode ser null fora do provider. */
export const useModuleSearchControl = (): ModuleSearchContextValue | null => useContext(ModuleSearchContext);

/**
 * Página de listagem chama isto para expor sua busca à appbar mobile.
 * `onChange` pode mudar de identidade a cada render — guardamos em ref para
 * não re-registrar à toa; só `placeholder`/`value` disparam atualização.
 */
export const useModuleSearch = (config: ModuleSearchConfig): void => {
  const ctx = useContext(ModuleSearchContext);
  const register = ctx?.register;
  const unregister = ctx?.unregister;
  const onChangeRef = useRef(config.onChange);
  onChangeRef.current = config.onChange;
  const { placeholder, value } = config;

  // register/unregister são useCallback([]) estáveis — NÃO usar o `ctx` inteiro
  // nas deps (ele é recriado a cada render do provider → loop infinito).
  useEffect(() => {
    if (!register) return;
    register({ placeholder, value, onChange: (v: string) => onChangeRef.current(v) });
  }, [register, placeholder, value]);

  useEffect(() => {
    if (!unregister) return;
    return () => unregister();
  }, [unregister]);
};

/**
 * Botão de busca da appbar mobile. Abre a busca do módulo quando a rota atual
 * registrou uma; senão, cai no CommandPalette de navegação (fallback).
 */
export const AppbarSearchButton: React.FC = () => {
  const ctx = useModuleSearchControl();
  const handleClick = useCallback(() => {
    if (ctx?.config) {
      ctx.open();
    } else {
      window.dispatchEvent(new Event('troia:open-command-palette'));
    }
  }, [ctx]);

  return (
    <button
      onClick={handleClick}
      className="w-[38px] h-[38px] flex items-center justify-center rounded-[11px] flex-none"
      style={{ color: 'var(--theme-card-text)' }}
      aria-label="Buscar"
      data-testid="mobile-appbar-search"
    >
      <Icon name="search" className="h-[22px] w-[22px]" />
    </button>
  );
};

/**
 * Barra de busca do módulo no topo (mobile). Substitui a appbar enquanto
 * aberta; a lista abaixo filtra ao vivo. Fecha com Esc, ✕ ou "Cancelar".
 */
export const ModuleSearchOverlay: React.FC = () => {
  const ctx = useModuleSearchControl();
  const inputRef = useRef<HTMLInputElement>(null);
  const isOpen = ctx?.isOpen ?? false;
  const config = ctx?.config ?? null;

  useEffect(() => {
    if (isOpen && inputRef.current) {
      const id = window.setTimeout(() => inputRef.current?.focus(), 40);
      return () => window.clearTimeout(id);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') ctx?.close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, ctx]);

  if (!isOpen || !config || !ctx) return null;

  return (
    <div
      className="min-[900px]:hidden fixed top-0 inset-x-0 z-[110] flex items-center gap-2 px-3"
      style={{
        height: '54px',
        // Overlay sobre a m-appbar — tint sobre base opaca
        background: 'var(--theme-overlay-header-background)',
        borderBottom: '1px solid var(--theme-header-border)',
      }}
      data-testid="module-search-overlay"
    >
      <div
        className="flex-1 flex items-center gap-2 h-[38px] px-3 rounded-[11px]"
        style={{
          backgroundColor: 'var(--theme-input-background)',
          border: '1px solid var(--theme-input-border)',
        }}
      >
        <Icon name="search" className="h-[18px] w-[18px] flex-none" style={{ color: 'var(--theme-muted-text)' }} />
        <input
          ref={inputRef}
          value={config.value}
          onChange={(e) => config.onChange(e.target.value)}
          placeholder={config.placeholder}
          className="flex-1 min-w-0 bg-transparent outline-none text-[15px]"
          style={{ color: 'var(--theme-input-text)' }}
          data-testid="module-search-input"
        />
        {config.value && (
          <button
            onClick={() => config.onChange('')}
            className="flex-none"
            style={{ color: 'var(--theme-muted-text)' }}
            aria-label="Limpar busca"
          >
            <Icon name="x" className="h-[16px] w-[16px]" />
          </button>
        )}
      </div>
      <button
        onClick={() => ctx.close()}
        className="flex-none text-[15px] font-medium px-1"
        style={{ color: 'var(--theme-primary)' }}
        data-testid="module-search-cancel"
      >
        Cancelar
      </button>
    </div>
  );
};
